import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Edit, Trash2, Lock, ShieldCheck } from "lucide-react";
import { managerPhotoUrl } from "./completionPhotoApi";
import { AuthImage, openAuthImage } from "./AuthImage";

export const DefectViewDialog = ({
  open,
  onOpenChange,
  defect,
  contractors,
  isViewOnly,
  canCloseOut,
  getStatusColor,
  getPriorityColor,
  onEdit,
  onCloseOut,
  onDelete,
}) => {
  if (!defect) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="defect-view-title">
            Defect Details - {defect.number}
          </DialogTitle>
          <DialogDescription>
            View detailed information about this defect.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-600">Area</Label>
              <p className="font-medium">{defect.area}</p>
            </div>
            <div>
              <Label className="text-slate-600">Element</Label>
              <p className="font-medium">{defect.element}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-600">Issue</Label>
              <p className="font-medium">{defect.issue}</p>
            </div>
            <div>
              <Label className="text-slate-600">Priority</Label>
              <Badge className={`${getPriorityColor(defect.priority)}`}>
                {defect.priority}
              </Badge>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-600">Status</Label>
              <Badge className={`${getStatusColor(defect.status)}`}>
                {defect.status.replace("_", " ")}
              </Badge>
            </div>
            {defect.due_date && (
              <div>
                <Label className="text-slate-600">Due Date</Label>
                <p className="font-medium">{defect.due_date}</p>
              </div>
            )}
          </div>
          {defect.closed_out && (
            <div
              className="rounded-md border border-slate-200 bg-slate-50 p-3"
              data-testid="defect-closeout-info"
            >
              <div className="flex items-center gap-2 text-slate-800 font-medium">
                <Lock className="w-4 h-4" /> Closed Out
              </div>
              <p className="text-sm text-slate-600 mt-1">
                By {defect.closed_out_by_name || "Unknown"}
                {defect.closed_out_at &&
                  ` on ${new Date(defect.closed_out_at).toLocaleString("en-AU")}`}
              </p>
            </div>
          )}
          {defect.assigned_to && (
            <div>
              <Label className="text-slate-600">Assigned To</Label>
              <p className="font-medium">
                {contractors.find((c) => c.id === defect.assigned_to)?.name ||
                  "Unknown"}
              </p>
            </div>
          )}
          {defect.details && (
            <div>
              <Label className="text-slate-600">Details</Label>
              <p className="text-sm">{defect.details}</p>
            </div>
          )}
          {defect.images && defect.images.length > 0 && (
            <div>
              <Label className="text-slate-600">Photos</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {defect.images.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`Defect ${index + 1}`}
                    className="w-full h-32 object-cover rounded border cursor-pointer"
                    onClick={() => window.open(img, "_blank")}
                  />
                ))}
              </div>
            </div>
          )}
          {defect.completion_photos?.length > 0 && (
            <div data-testid="view-completion-photos">
              <Label className="text-slate-600">Completion photos</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {defect.completion_photos.map((p) => (
                  <div key={p.id} className="relative">
                    <AuthImage src={managerPhotoUrl(defect.id, p.id)} alt="Completion"
                      className="w-full h-32 object-cover rounded border cursor-pointer"
                      onClick={() => openAuthImage(managerPhotoUrl(defect.id, p.id, false))} />
                    {p.client_approved && (
                      <span className="absolute bottom-1 left-1 text-[10px] font-bold rounded bg-indigo-600 text-white px-1.5 py-0.5">Client</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {defect.reopen_history?.length > 0 && (
            <div data-testid="view-reopen-history">
              <Label className="text-slate-600">Reopened</Label>
              <ul className="mt-1 space-y-1 text-sm text-slate-700">
                {defect.reopen_history.map((h, i) => (
                  <li key={i}>{new Date(h.at).toLocaleString("en-AU")} — {h.by_name}: “{h.reason}”</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          {!isViewOnly && (
            <>
              <Button
                variant="outline"
                className="text-blue-600 border-blue-200 hover:text-blue-700"
                onClick={() => {
                  onOpenChange(false);
                  onEdit(defect);
                }}
                data-testid="view-dialog-edit-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Defect
              </Button>
              {defect.status === "completed" &&
                !defect.closed_out &&
                canCloseOut && (
                  <Button
                    variant="outline"
                    className="text-green-600 border-green-200 hover:text-green-700"
                    onClick={() => {
                      onOpenChange(false);
                      onCloseOut(defect.id);
                    }}
                    data-testid="view-dialog-closeout-btn"
                  >
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Close Out
                  </Button>
                )}
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:text-red-700"
                onClick={() => {
                  onOpenChange(false);
                  onDelete(defect.id);
                }}
                data-testid="view-dialog-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </>
          )}
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
